import { DateTime, Duration, Interval, Settings, type Zone } from 'luxon';

Settings.defaultZone = 'UTC';
Settings.defaultLocale = DateTime.now().resolvedLocaleOptions().locale;

export function getTimezoneAwareCurrentDate(timezone?: string): DateTime<true> {
  const currentTime = DateTime.now().setZone(timezone);
  if (!currentTime.isValid) {
    throw new Error('Invalid current time', {
      cause: new Error(
        `${currentTime.invalidReason} ${currentTime.invalidExplanation}`
      ),
    });
  }

  return currentTime;
}

export function anchorCalendarDateInTimezone(
  date: DateTime<true>,
  timezone: string,
  options?: { preserveTime?: boolean }
): DateTime<true> {
  if (options?.preserveTime) {
    return date.setZone(timezone, { keepLocalTime: true }) as DateTime<true>;
  }

  return DateTime.fromObject(
    {
      year: date.year,
      month: date.month,
      day: date.day,
    },
    { zone: timezone }
  ) as DateTime<true>;
}

export function preserveCalendarRangeInTimezone(
  range: [DateTime<true>, DateTime<true>],
  timezone: string,
  options?: { preserveTime?: boolean }
): [DateTime<true>, DateTime<true>] {
  const [start, end] = range;
  const startDate = anchorCalendarDateInTimezone(start, timezone, options);
  const endDate = anchorCalendarDateInTimezone(end, timezone, options);

  return [
    options?.preserveTime ? startDate : startDate.startOf('day'),
    options?.preserveTime ? endDate : endDate.endOf('day'),
  ];
}

export function parseISO8601DateTime(date: string, timezone?: string | Zone) {
  return DateTime.fromISO(date, { zone: timezone });
}

export function formatISOString(date: string) {
  return DateTime.fromISO(date).toFormat('MM/dd/yyyy, hh:mm a');
}

export function parseUnixTimestamp(seconds: number, timezone?: string) {
  return DateTime.fromSeconds(seconds, {
    zone: timezone,
  });
}

export function parseISO8601Duration(duration: string) {
  return Duration.fromISO(duration);
}

export function dateTimeFromFormat(
  time: string,
  format: string,
  timezone?: string
) {
  const opts = timezone ? { zone: timezone } : undefined;
  return DateTime.fromFormat(time, format, opts);
}

// Week starts on Sunday in the USA, not on Monday, so we're hacking luxon a little bit here
// to get the correct start of the week. This is a known issue with luxon:
// https://github.com/moment/luxon/issues/373
export function getWeekStart<T extends boolean>(
  date: DateTime<T>
): DateTime<T> {
  return date.startOf('week', { useLocaleWeeks: true });
}

export function getEndOfWeek<T extends boolean>(
  date: DateTime<T>
): DateTime<T> {
  return date.endOf('week', { useLocaleWeeks: true });
}

export function filterByDateRange<T>(
  data: Array<T>,
  startDate: DateTime,
  endDate: DateTime,
  dateField: ((arg0: T) => DateTime) | string = 'date'
) {
  const interval = Interval.fromDateTimes(
    startDate.startOf('day'),
    endDate.endOf('day')
  );

  return data.filter((item) => {
    let dateValue: string;
    if (typeof dateField === 'function') {
      const result = dateField(item);
      dateValue =
        typeof result === 'string'
          ? result
          : (result as DateTime<true>)?.toISO?.() || '';
    } else {
      const rawValue = (item as Record<string, unknown>)[dateField as string];
      if (typeof rawValue === 'string') {
        dateValue = rawValue;
      } else if (
        rawValue &&
        typeof rawValue === 'object' &&
        'toISO' in rawValue &&
        typeof rawValue.toISO === 'function'
      ) {
        dateValue = (rawValue as DateTime<true>).toISO();
      } else {
        dateValue = '';
      }
    }

    return interval.contains(parseISO8601DateTime(dateValue));
  });
}

export function isBetweenDates(
  start: Date,
  end: Date,
  value: DateTime
): boolean {
  const interval = Interval.fromDateTimes(
    DateTime.fromJSDate(start).startOf('day'),
    DateTime.fromJSDate(end).endOf('day')
  );

  return interval.contains(value);
}

export function fromJSDate(
  date: Date,
  options?: { zone?: string | Zone }
): DateTime<true> {
  const parsedDate = DateTime.fromJSDate(date, options);
  if (!parsedDate.isValid) {
    throw new Error('invalid date parsed');
  }
  return parsedDate;
}

export const DATE_FULL: Intl.DateTimeFormatOptions = {
  ...DateTime.DATE_FULL,
};

export const DATETIME_FULL: Intl.DateTimeFormatOptions = {
  ...DateTime.DATETIME_MED_WITH_WEEKDAY,
};

export const DATETIME_FULL_WITH_SECONDS: Intl.DateTimeFormatOptions = {
  ...DateTime.DATETIME_MED_WITH_WEEKDAY,
  second: '2-digit',
};

export const DATETIME_SHORT_2_DIGIT_MONTH_AND_DAY_WITH_SECONDS: Intl.DateTimeFormatOptions =
  {
    ...DateTime.DATETIME_SHORT_WITH_SECONDS,
    month: '2-digit',
    day: '2-digit',
  };

export const DATE_SHORT_WORD_MONTH: Intl.DateTimeFormatOptions = {
  ...DateTime.DATE_SHORT,
  month: 'long', // Use 'long' for full month name (e.g., "January")
  day: '2-digit',
};

export const TIME_WITH_SECONDS: Intl.DateTimeFormatOptions = {
  ...DateTime.TIME_WITH_SECONDS,
};

export function isToday(dt: DateTime): boolean {
  const currentDateTime = getTimezoneAwareCurrentDate();

  // Even though the name may be a misleading, hasSame(DateTime,"day") doesn't only compare days it also makes sure the "Higher-order"
  // units are identical. So the date also has the same week, month and year.
  // For reference: https://idkshite.com/posts/luxon-same-day
  return currentDateTime.hasSame(dt, 'day');
}

export function formatInTimezone<T extends boolean>(
  dt: DateTime<T>,
  timezone: string,
  format: Intl.DateTimeFormatOptions = DATETIME_FULL
): string {
  return dt.setZone(timezone).toLocaleString(format);
}

export function getTimezoneLabel(timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find((part) => part.type === 'timeZoneName');
  return tzPart?.value ?? timezone;
}

export { DateTime, Duration } from 'luxon';
